import type { TimeInput } from './types';
import { toEpochMs } from './isomorphic/internal/coerce';

/** Unit thresholds in ms, largest first. */
const UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000000],
    ['month', 2592000000],
    ['day', 86400000],
    ['hour', 3600000],
    ['minute', 60000],
    ['second', 1000],
];

export interface FormatRelativeOptions {
    locale?: string;
    /** `auto` yields "yesterday"/"tomorrow"; `always` yields "1 day ago". Default `auto`. Ignored by `compact`. */
    numeric?: 'auto' | 'always';
    /**
     * `long` (default) is the locale's own wording — "2 hours ago".
     * `compact` is the dense-list stamp — "23m", "8h", "3d", "in 5m", "now"
     * under a minute (BL-1780, the clerk inbox row). Compact FLOORS rather
     * than rounds, so a unit never shows its own ceiling ("60m", "24h").
     */
    style?: 'long' | 'compact';
}

/** Compact suffix per unit. Seconds never render — under a minute is "now". */
const COMPACT_SUFFIX: Partial<Record<Intl.RelativeTimeFormatUnit, string>> = {
    year: 'y',
    month: 'mo',
    day: 'd',
    hour: 'h',
    minute: 'm',
};

/** The compact stamp for a signed distance in ms (`diff` = target − now). */
function formatCompact(diff: number): string {
    const abs = Math.abs(diff);
    for (const [unit, ms] of UNITS) {
        const suffix = COMPACT_SUFFIX[unit];
        if (suffix && abs >= ms) {
            const label = `${Math.floor(abs / ms)}${suffix}`;
            return diff > 0 ? `in ${label}` : label;
        }
    }
    return 'now';
}

/**
 * Format an instant relative to a caller-supplied `nowMs` ("in 5 minutes",
 * "2 hours ago", or compact "23m" — see {@link FormatRelativeOptions.style}). `nowMs` is a parameter — not `Date.now()` — so it's pure and
 * can be fed a server-anchored now and is trivially testable.
 *
 * @sideEffect none — pure.
 */
export function formatRelative(
    instant: TimeInput,
    nowMs: number,
    opts: FormatRelativeOptions = {},
): string {
    const target = toEpochMs(instant);
    if (target == null) return '';

    const diff = target - nowMs;
    if (opts.style === 'compact') return formatCompact(diff);

    const rtf = new Intl.RelativeTimeFormat(opts.locale ?? 'en-US', {
        numeric: opts.numeric ?? 'auto',
    });

    const abs = Math.abs(diff);
    for (const [unit, ms] of UNITS) {
        if (abs >= ms || unit === 'second') {
            return rtf.format(Math.round(diff / ms), unit);
        }
    }
    return rtf.format(0, 'second');
}
